import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Users, Search, CheckCircle2, XCircle } from 'lucide-react';

export default function UsersManager() {
    const [waiters, setWaiters] = useState([]);
    const [establishmentsMap, setEstablishmentsMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Parallel fetch for waiters and establishments lookup map
            const [waitersRes, estRes] = await Promise.all([
                supabase.from('waiters').select('*').order('created_at', { ascending: false }),
                supabase.from('establishments').select('id, name')
            ]);

            if (waitersRes.error) throw waitersRes.error;
            if (estRes.error) throw estRes.error;

            // Build a lookup map for faster rendering O(1) instead of nested loops
            const estMap = {};
            estRes.data?.forEach(est => {
                estMap[est.id] = est.name;
            });

            setEstablishmentsMap(estMap);
            setWaiters(waitersRes.data || []);
        } catch (error) {
            console.error('Error fetching waiters data:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = waiters.filter(waiter =>
        waiter.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiter.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <h1 style={{ fontSize: '1.75rem', fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Users color="var(--accent-primary)" /> Staff Members
                    </h1>
                    <p style={{ color: 'var(--text-secondary)' }}>View all registered staff members across the network.</p>
                </div>

                <div style={{ position: 'relative', width: '300px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                    <input
                        type="text"
                        placeholder="Search by name or establishment..."
                        className="form-input"
                        style={{ paddingLeft: '40px' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Staff Name</th>
                                <th>Contact</th>
                                <th>Assigned Restaurant</th>
                                <th>Login PIN</th>
                                <th>Verified</th>
                                <th>Joined Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '32px' }}>Loading...</td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>No staff members found.</td>
                                </tr>
                            ) : (
                                filteredData.map((waiter) => (
                                    <tr key={waiter.id}>
                                        <td style={{ fontWeight: 600 }}>{waiter.name}</td>
                                        <td>{waiter.email || '-'}</td>
                                        <td>
                                            <span style={{ background: 'var(--bg-app)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', border: '1px solid var(--border-light)' }}>
                                                {establishmentsMap[waiter.establishment_id] || 'Unknown Restaurant'}
                                            </span>
                                        </td>
                                        <td><code style={{ background: 'var(--bg-app)', padding: '4px 8px', borderRadius: '4px', letterSpacing: '1px' }}>••••</code></td>
                                        <td>
                                            {waiter.is_verified ? (
                                                <CheckCircle2 color="var(--accent-success)" size={20} />
                                            ) : (
                                                <XCircle color="var(--text-tertiary)" size={20} />
                                            )}
                                        </td>
                                        <td>{new Date(waiter.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
